import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Search, UserPlus, RefreshCw, ShieldOff, GraduationCap, Handshake, UserCheck, Ban } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_LABEL = {
  aluno: { label: 'Aluno / Ex-aluno', cls: 'bg-blue-600/20 text-blue-300 border-blue-500/30', Icon: GraduationCap },
  professor: { label: 'Professor', cls: 'bg-purple-600/20 text-purple-300 border-purple-500/30', Icon: UserCheck },
  parceiro: { label: 'Parceiro', cls: 'bg-emerald-600/20 text-emerald-300 border-emerald-500/30', Icon: Handshake },
  convidado: { label: 'Convidado', cls: 'bg-orange-600/20 text-orange-300 border-orange-500/30', Icon: UserPlus },
};

const AdminConnectAccess = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [grantEmail, setGrantEmail] = useState('');
  const [grantReason, setGrantReason] = useState('parceiro');
  const [busy, setBusy] = useState(false);

  const fetchUsers = useCallback(async (term = '') => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/connect/access`, { params: term ? { search: term } : {} });
      setUsers(res.data.users || []);
    } catch {
      toast.error('Erro ao carregar acessos do Connect.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleGrant = async () => {
    if (!grantEmail.trim()) { toast.error('Informe o e-mail.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/connect/grant`, { email: grantEmail.trim(), reason: grantReason });
      toast.success(res.data.message || 'Acesso concedido.');
      setGrantEmail('');
      fetchUsers(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao conceder acesso.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (email) => {
    if (!window.confirm(`Descadastrar ${email} do ambiente Connect? O acesso gratuito será removido.`)) return;
    try {
      const res = await axios.post(`${API}/admin/connect/revoke`, { email });
      toast.success(res.data.message || 'Acesso removido.');
      fetchUsers(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover acesso.');
    }
  };

  const handleSync = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/connect/sync`);
      toast.success(`Sincronizado: ${res.data.granted} concedidos, ${res.data.removed} removidos.`);
      fetchUsers(search);
    } catch {
      toast.error('Erro ao sincronizar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-connect-access">
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4">
        <p className="text-sm text-gray-400 mb-4">
          Alunos, ex-alunos (com matrícula) e professores recebem o acesso ao AJ | BIM Connect
          <span className="text-white font-medium"> automaticamente e grátis</span>. Aqui você pode conceder acesso a
          parceiros/convidados (que já tenham conta), marcar professores e descadastrar quem precisar.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-gray-400 block mb-1">E-mail da pessoa (conta já existente)</label>
            <Input
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="email@exemplo.com"
              className="bg-zinc-800 border-zinc-700 text-white"
              data-testid="grant-email-input"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Tipo</label>
            <select
              value={grantReason}
              onChange={(e) => setGrantReason(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 text-white rounded-md h-10 px-3 text-sm"
              data-testid="grant-reason-select"
            >
              <option value="parceiro">Parceiro</option>
              <option value="convidado">Convidado</option>
              <option value="professor">Professor</option>
            </select>
          </div>
          <Button onClick={handleGrant} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid="grant-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Conceder acesso
          </Button>
          <Button onClick={handleSync} disabled={busy} variant="outline" className="border-zinc-700 text-gray-300" data-testid="sync-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Sincronizar alunos
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchUsers(search)}
            placeholder="Buscar por nome ou e-mail..."
            className="bg-zinc-800 border-zinc-700 text-white pl-9"
            data-testid="connect-search-input"
          />
        </div>
        <Button onClick={() => fetchUsers(search)} variant="outline" className="border-zinc-700 text-gray-300">Buscar</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-red-500" size={28} /></div>
      ) : (
        <div className="space-y-2">
          {users.length === 0 && <p className="text-gray-500 text-sm text-center py-6">Nenhum usuário encontrado.</p>}
          {users.map((u) => {
            const info = u.free_reason ? REASON_LABEL[u.free_reason] : null;
            const Icon = info?.Icon;
            return (
              <Card key={u.user_id} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white font-medium truncate">{u.name || '(sem nome)'}</div>
                    <div className="text-gray-500 text-sm truncate">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {u.connect_blocked ? (
                      <Badge className="bg-red-600/20 text-red-300 border-red-500/30"><Ban className="w-3 h-3 mr-1" />Descadastrado</Badge>
                    ) : info ? (
                      <Badge className={info.cls}>{Icon && <Icon className="w-3 h-3 mr-1" />}{info.label} · grátis</Badge>
                    ) : (
                      <Badge className="bg-zinc-700/40 text-gray-400 border-zinc-600/40">Sem acesso gratuito</Badge>
                    )}
                    {u.has_profile && (
                      <span className="text-xs text-gray-500">plano: {u.profile_plan}</span>
                    )}
                    {(u.free_reason || u.connect_free || u.is_professor) && !u.connect_blocked && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRevoke(u.email)}
                        className="border-red-600/40 text-red-400 hover:bg-red-600/10 h-8"
                        data-testid={`revoke-${u.email}`}
                      >
                        <ShieldOff className="w-3.5 h-3.5 mr-1" /> Descadastrar
                      </Button>
                    )}
                    {u.connect_blocked && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setGrantEmail(u.email); toast.info('Escolha o tipo acima e clique em Conceder acesso para reativar.'); }}
                        className="border-zinc-700 text-gray-300 h-8"
                      >
                        Reativar
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminConnectAccess;
